/**
 * Member Billing Info Form Component
 *
 * Everything used to identify this clinician on insurance claims and clinical
 * documents, grouped by purpose:
 *   1. Professional Identity — license, NPI, taxonomy
 *   2. Claim Defaults        — billing identity, service facility
 *   3. Display Names         — patient-facing + signature names
 *   4. Contact for Documents — phone/fax printed on lab orders & letterheads
 */

import { NpiValidationStatus } from '@features/npi-validation';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import type { ProviderLicenseType } from '@store/generated/organizations';
import { PROVIDER_LICENSE_TYPE_OPTIONS } from '@utils/providerLicenseTypes';
import { MuiTelInput } from 'mui-tel-input';

interface BillingInfoFormData {
  billing_entity_id?: string | null;
  service_facility_id?: string | null;
  taxonomy_code?: string | null;
  license_type?: ProviderLicenseType | null;
  license_number?: string | null;
  license_state?: string | null;
  npi_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  communications_name?: string | null;
  signing_name?: string | null;
  facility_address?: string | null;
  facility_address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  fax_number?: string | null;
  tax_id?: string | null;
  tax_id_type?: string | null;
  place_of_service?: string | null;
}

interface MemberBillingInfoFormProps {
  data: BillingInfoFormData;
  onChange: (field: keyof BillingInfoFormData, value: string) => void;
  disabled?: boolean;
  billingEntities?: BillingEntityResponse[];
  entitiesLoading?: boolean;
  serviceFacilities?: ServiceFacilityResponse2[];
  facilitiesLoading?: boolean;
}

function SectionHeader({ title, intro }: { title: string; intro: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {intro}
      </Typography>
    </Box>
  );
}

export function MemberBillingInfoForm({
  data,
  onChange,
  disabled = false,
  billingEntities = [],
  entitiesLoading = false,
  serviceFacilities = [],
  facilitiesLoading = false,
}: MemberBillingInfoFormProps) {
  // Get the default entity/facility for display
  const defaultEntity = billingEntities.find(e => e.is_default);
  const defaultFacility = serviceFacilities.find(f => f.is_default);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 1. Professional Identity */}
      <Box>
        <SectionHeader
          title="Professional Identity"
          intro="Identifies this clinician on insurance claims and clinical documents."
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>License Type</InputLabel>
            <Select
              value={data.license_type || ''}
              onChange={e => onChange('license_type', e.target.value)}
              disabled={disabled}
              label="License Type"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {PROVIDER_LICENSE_TYPE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="License Number"
              value={data.license_number || ''}
              onChange={e => onChange('license_number', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
            <TextField
              label="License State"
              value={data.license_state || ''}
              onChange={e => onChange('license_state', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
              placeholder="CA"
            />
          </Box>

          <Box>
            <TextField
              label="NPI Number"
              value={data.npi_number || ''}
              onChange={e => onChange('npi_number', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
              helperText="The clinician's own 10-digit individual (Type 1) NPI. Leave blank if they bill under a supervising provider — see the Supervision tab."
            />
            <NpiValidationStatus npi={data.npi_number} expectedType="individual" />
          </Box>

          <TextField
            label="Taxonomy Code"
            value={data.taxonomy_code || ''}
            onChange={e => onChange('taxonomy_code', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            placeholder="e.g., 2084P0800X"
            helperText="Specialty code sent with their NPI on claims (10-char alphanumeric ending in X)"
            slotProps={{ htmlInput: { maxLength: 10 } }}
          />
        </Box>
      </Box>

      <Divider />

      {/* 2. Claim Defaults */}
      <Box>
        <SectionHeader
          title="Claim Defaults"
          intro="Used when this clinician's claims are built. Leave blank to use your practice's defaults."
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Billing Identity</InputLabel>
            <Select
              value={data.billing_entity_id || ''}
              onChange={e => onChange('billing_entity_id', e.target.value || '')}
              disabled={disabled || entitiesLoading}
              label="Billing Identity"
              startAdornment={
                entitiesLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null
              }
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <em>Use practice default</em>
                  {defaultEntity && (
                    <Typography variant="caption" color="text.secondary">
                      ({defaultEntity.legal_name})
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              {billingEntities.map(entity => (
                <MenuItem key={entity.billing_entity_id} value={entity.billing_entity_id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>{entity.legal_name}</span>
                    <Typography variant="caption" color="text.secondary">
                      (NPI: {entity.billing_npi})
                    </Typography>
                    {entity.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, ml: 'auto' }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              The practice entity their claims are billed from (Box 33).
            </Typography>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Service Facility</InputLabel>
            <Select
              value={data.service_facility_id || ''}
              onChange={e => onChange('service_facility_id', e.target.value || '')}
              disabled={disabled || facilitiesLoading}
              label="Service Facility"
              startAdornment={
                facilitiesLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null
              }
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <em>Use practice default</em>
                  {defaultFacility && (
                    <Typography variant="caption" color="text.secondary">
                      ({defaultFacility.display_name})
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              {serviceFacilities.map(facility => (
                <MenuItem key={facility.service_facility_id} value={facility.service_facility_id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>{facility.display_name}</span>
                    {facility.address?.city && (
                      <Typography variant="caption" color="text.secondary">
                        ({facility.address.city}, {facility.address.state})
                      </Typography>
                    )}
                    {facility.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, ml: 'auto' }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              The location where this clinician sees patients (Box 32).
            </Typography>
          </FormControl>
        </Box>
      </Box>

      <Divider />

      {/* 3. Display Names */}
      <Box>
        <SectionHeader
          title="Display Names"
          intro="How this clinician's name appears to patients and on documents. Their account name lives in Basic Info."
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Patient-Facing Name"
            value={data.communications_name || ''}
            onChange={e => onChange('communications_name', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            helperText="Shown to patients in emails, texts, and the waiting room (e.g., 'Dr. Doe')"
          />

          <TextField
            label="Signature Name"
            value={data.signing_name || ''}
            onChange={e => onChange('signing_name', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            helperText="Credentialed name on signed notes and documents (e.g., 'John Doe, LCSW')"
          />
        </Box>
      </Box>

      <Divider />

      {/* 4. Contact for Documents */}
      <Box>
        <SectionHeader
          title="Contact for Documents"
          intro="Printed on lab orders and document letterheads."
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <MuiTelInput
            label="Phone Number"
            value={data.phone || ''}
            onChange={value => onChange('phone', value)}
            disabled={disabled}
            fullWidth
            size="small"
            defaultCountry="US"
            forceCallingCode
          />
          <MuiTelInput
            label="Fax Number"
            value={data.fax_number || ''}
            onChange={value => onChange('fax_number', value)}
            disabled={disabled}
            fullWidth
            size="small"
            defaultCountry="US"
            forceCallingCode
          />
        </Box>
      </Box>
    </Box>
  );
}
